import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { container } from '@/services/container';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import type { Address } from '@shared/chain';

export interface AppealDialogProps {
  open: boolean;
  onClose: () => void;
  defaultCid?: string;
}

/**
 * 作者申诉弹窗（治理 v2 人性化出口）：软下架内容在窗口内（默认 144 块）由作者发起链上申诉，
 * 进入仲裁后由运营裁决（upheld=维持下架 / dismissed=误报恢复并惩戒举报人）。
 * 链端校验：仅内容作者可申诉；窗口过期/未下架会拒绝并提示。
 */
export function AppealDialog({ open, onClose, defaultCid = '' }: AppealDialogProps): JSX.Element {
  const [cid, setCid] = useState(defaultCid);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    const target = cid.trim();
    if (!target) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setSubmitting(true);
    try {
      const res = await container.chain.appealContent({
        from: container.identity.address as Address,
        cid: target,
        signature: '',
      });
      useUiStore.getState().pushToast(
        'success',
        res.appeal ? '申诉已提交链上，等待仲裁复核' : '申诉状态未变更（可能已提交过）',
      );
      onClose();
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
      // 错误原因透传给用户（仅内容作者可申诉 / 申诉窗口已过 / 未被下架）
      const msg = err instanceof MeshLinkError ? err.message : String(err);
      useUiStore.getState().pushToast('error', msg || '申诉提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="内容申诉"
      description="你的内容被举报确认下架后，可在申诉窗口内提交链上申诉，由仲裁复核是否误报。"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button size="sm" loading={submitting} onClick={() => void submit()}>
            提交申诉
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block text-xs text-dim">
          内容 CID（被下架帖子的存证 ID）
          <div className="mt-1">
            <Input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="输入帖子 CID" />
          </div>
        </label>
        <p className="text-xs text-dim">
          仅内容作者本人可申诉；申诉受理后内容保持不可见，等待仲裁裁决。误报被驳回时举报人会受到信誉与保证金惩戒。
        </p>
      </div>
    </Modal>
  );
}

export default AppealDialog;
